import React, { useEffect, useState } from "react";
import ContentHeading from "../../../components/layout/content/ContentHeading";
import InputTextField from "../../../components/input/inputTextField";
import TableCustom from "../../../components/table/tableCustom";
import TableRowCommon from "../../../components/table/TableRowCommon";
import { useDispatch } from "react-redux";
import { NavigateFunction, useNavigate } from "react-router-dom";
import { SearchOutlined } from "@mui/icons-material";
import { debouncedApiCall } from "../../../utils/app.utils";
import { CourseAnnualDetailAPI } from "../../../api/course-annual-detail/course-annual-detail.api";
import { dateToView } from "../../../utils/date.utils";
import { icons } from "../../../assets/icons/icons";
import {
  alertActive,
  alertError,
  alertSuccess,
} from "../../../components/alert/swal";
import { setLoading, unsetLoading } from "../../../store/loading/loading.slice";
import {
  ButtonPrimary,
} from "../../../components/button/ButtonCustoms";
import { BookingMeetingRoomRequestApi } from "../../../api/booking-meeting-room-request/booking-meeting-room-request.api";
import { routeName } from "../../../routes/routeName";
import StatusCustom from "../../../components/status/statusCustom";
import { useMediaQuery, useTheme } from "@mui/material";
import { booking_meeting_room_status } from "../../home/classroom-meeting-booking/enum";
import { useTranslation } from "react-i18next";

type Props = {};

const BookingServiceFeesManagement: React.FC<Props> = (props: Props) => {
  const { t } = useTranslation();
  const navigate: NavigateFunction = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up('md'));

  const [data, setData] = useState<any[]>([]);

  const [search, setSearch] = useState<string>("");

  const [page, setPage] = useState<number>(1);
  const [pageLimit, setPageLimit] = useState<number>(10);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [sortBy, setSortBy] = useState<string>("");
  const [sortType, setSortType] = useState<string>("ASC");
  const handleRequestSort = (sortBy: string, sortType: string) => {
    setSortBy(sortBy);
    setSortType(sortType);
  };

  const getData = async () => {
    let query: any = {
      page: page,
      limit: pageLimit,
      sortBy: sortBy,
      sortType: sortType,
      keyword: search ?? "",
    };

    const res = await BookingMeetingRoomRequestApi.getMeetingRoomRequest(query);
    const data = res.data;
    if (data.status < 300) {
      setData(res.data?.data?.items);
      setTotalItems(res.data?.data?.meta?.totalItems || 0);
    } else {
      setData([]);
    }
  };

  const handleDelete = async (id_service_fee: number) => {
    if (id_service_fee) {
      alertActive(
        t("ลบรายงานการใช้ห้องเรียน/ห้องประชุม"),
        t("ท่านต้องการลบรายงานการใช้ห้องเรียน/ห้องประชุม ใช่หรือไม่"),
        undefined,
        undefined,
        async () => {
          dispatch(setLoading());
          await CourseAnnualDetailAPI.deleteCourseAnnualDetailById(
            +id_service_fee
          )
            .then((res) => {
              if (res.status < 300) {
                alertSuccess(
                  t("ลบรายงานการใช้ห้องเรียน/ห้องประชุม เรียบร้อย"),
                  undefined,
                  undefined,
                  () => {
                    getData();
                  }
                );
              }
            })
            .catch(() => {
              alertError(t("ลบรายงานการใช้ห้องเรียน/ห้องประชุม ไม่สำเร็จ"));
            })
            .finally(() => {
              dispatch(unsetLoading());
            });
        }
      );
    }
    return;
  };

  const headCells = [
    {
      id: "no",
      width: 50,
      sortable: false,
      disablePadding: false,
      align: "center",
      label: t("ลำดับ"),
    },
    {
      id: "ประเภทห้อง", // i18n-ignore: column id (not displayed)
      width: 100,
      sortable: false,
      disablePadding: false,
      align: "center",
      label: t("ประเภทห้อง"),
    },
    {
      id: "ชื่อห้อง", // i18n-ignore: column id (not displayed)
      width: 150,
      sortable: false,
      disablePadding: false,
      align: "center",
      label: t("ชื่อห้อง"),
    },
    {
      id: "ช่วงวันที่จอง", // i18n-ignore: column id (not displayed)
      width: 100,
      sortable: false,
      disablePadding: false,
      align: "center",
      label: t("ช่วงวันที่จอง"),
    },
    {
      id: "ช่วงเวลา", // i18n-ignore: column id (not displayed)
      width: 100,
      sortable: false,
      disablePadding: false,
      align: "center",
      label: t("ช่วงเวลา"),
    },
    {
      id: "วันที่ดำเนินการ", // i18n-ignore: column id (not displayed)
      width: 100,
      sortable: false,
      disablePadding: false,
      align: "center",
      label: t("วันที่ดำเนินการ"),
    },
    {
      id: "สถานะ", // i18n-ignore: column id (not displayed)
      width: 75,
      sortable: false,
      disablePadding: false,
      align: "center",
      label: t("สถานะ"),
    },
    {
      id: "action",
      width: 40,
      sortable: false,
      disablePadding: false,
      class: matches && data?.length > 0,
      align: "start",
      label: "",
    },
  ];

  const renderData = (objData: any, no: any) => {
    no = page * pageLimit - pageLimit + no + 1;
    const {
      id_meeting_room,
      meeting_room_name,
      meeting_room_start_date,
      meeting_room_end_date,
      meeting_start_time,
      meeting_end_time,
      meeting_room_status,
      created_date,
      room_meeting_type,
    } = objData;

    const objRenderData = {
      key: id_meeting_room,
      id: no,
      obj: objData,
      columns: [
        {
          option: "TEXT",
          align: "start",
          label: no ?? "-",
        },
        {
          option: "TEXT",
          align: "start",
          label: room_meeting_type ? t(room_meeting_type) : "-",
        },
        {
          option: "TEXT",
          align: "start",
          label: meeting_room_name ?? "-",
        },
        {
          option: "TEXT",
          align: "center",
          label:
            meeting_room_start_date && meeting_room_end_date
              ? dateToView(meeting_room_start_date) +
              " - " +
              dateToView(meeting_room_end_date)
              : "",
        },
        {
          option: "TEXT",
          align: "center",
          label:
            meeting_start_time && meeting_end_time
              ? meeting_start_time + " - " + meeting_end_time
              : "",
        },
        {
          option: "TEXT",
          align: "center",
          label: created_date ? dateToView(created_date) : "",
        },
        {
          option: "COMPONENT",
          align: "start",
          component: (
            <div>
              <StatusCustom
                disableMaxWidth
                sx={{ width: 'fit-content', px: 2.5 }}
                text={t(booking_meeting_room_status?.find((x) => x.value == meeting_room_status)?.label || "")}
                type={booking_meeting_room_status?.find((x) => x.value == meeting_room_status)?.label || ""}
              />
            </div>
          ),
        },
        {
          option: "COMPONENT",
          align: "center",
          class: matches,
          component: (
            <div
              style={{
                display: "flex",
                textAlign: "center",
                justifyContent: "center",
              }}
            >
              <img
                onClick={() => {
                  navigate(
                    routeName.cms.BookingRoom +
                    `/${id_meeting_room}/update?mode=REPORT`
                  );
                }}
                src={icons.eye}
                alt="eye"
                style={{ cursor: "pointer" }}
              />
            </div>
          ),
        },
      ],
    };
    return <TableRowCommon {...objRenderData} />;
  };

  useEffect(() => {
    getData();
  }, [page, pageLimit, sortBy, sortType, search]);

  return (
    <div>
      <ContentHeading
        title={t("รายงานการใช้ห้องเรียน/ห้องประชุม")}
        detail={t("กรุณาเลือกรายการที่ท่านต้องการ")}
      />

      <div className="w-full grid grid-cols-12 gap-4 mt-[40px]">
        <article className="w-full col-span-12 lg:col-span-3 md:col-span-3 sm:col-span-3">
          <InputTextField
            startAdornment={<SearchOutlined />}
            placeholder={t("ค้นหา คำร้องขอจอง")}
            onchange={(e: React.ChangeEvent<HTMLInputElement>) =>
              debouncedApiCall({
                searchItem: e.target.value,
                setItem: setSearch,
              })
            }
          />
        </article>

        <article className="w-full col-span-12 lg:col-span-9 md:col-span-9 sm:col-span-9 flex justify-end">
          <ButtonPrimary
            onClick={() => { }}
            mode="excel"
            buttonName={t("ดาวน์โหลด Excel")}
          />

          <ButtonPrimary
            onClick={() => { }}
            mode="pdf"
            buttonName={t("ดาวน์โหลด PDF")}
          />
        </article>
      </div>

      <section className="w-full mt-10">
        <TableCustom
          tableMinWidth={1440}
          tableFixedWidth
          customScroll
          page={page}
          pageLimit={pageLimit}
          sortBy={sortBy}
          sortType={sortType}
          headCells={headCells}
          rowCount={totalItems}
          rowsData={
            data &&
            data.map((data: any, i: any) => {
              return renderData(data, i);
            })
          }
          onSort={handleRequestSort}
          setPageLimit={(value: number) => setPageLimit(value)}
          setPage={(value: number) => setPage(value)}
        />
      </section>
    </div>
  );
};

export default BookingServiceFeesManagement;
